/**
 * `placement` — the card's size, and (under "Advanced (developers)") the two
 * settings that need code on the partner's page. The layout (`placement.mode`)
 * is not shown: the widget only renders inline, so "launcher" would do
 * nothing; a stored value is kept.
 *
 * The Advanced group also says what the widget pushes to the page's
 * dataLayer. That push is unconditional in the widget (GTM decides what
 * fires), so `analytics.push_data_layer` is not offered as a switch.
 *
 * Size
 * (`placement.size`, contracts/widget-config.md §Card size): width is always a
 * CAP, never a fixed width, so no value can push a partner's page into
 * horizontal scroll on a phone. Height is ONE number: the widget is always a
 * fixed-height card (owner decision 2026-10-02), so the partner's page never
 * moves as a visitor goes from step to step. There is no grow-with-content
 * mode. The stored pair is written as min = max.
 */

type PxPath = 'placement.size.max_width_px' | 'placement.size.max_height_px' | 'placement.size.min_height_px';

import { CodeBlock, Disclosure, Field, Segmented } from '../../ui/kit';
import { CARD_HEIGHT_DEFAULT, SIZE_LIMITS, type PlacementSize } from '../model';
import { AdvancedDisclosure, PathSwitch, SectionCard, usePath } from './common';

function PxInput({ path, label, hint, disabled }: { path: PxPath; label: string; hint: string; disabled?: boolean }) {
  const { value, set, issues } = usePath<number | null>(path);
  const limits = SIZE_LIMITS[path.slice('placement.size.'.length) as keyof typeof SIZE_LIMITS];
  return (
    <Field label={label} hint={hint} issues={issues} optional>
      {(p) => (
        <input
          {...p}
          className="input num"
          type="number"
          min={limits.min}
          max={limits.max}
          step={10}
          disabled={disabled}
          value={value ?? ''}
          placeholder={disabled ? 'Not used' : 'None'}
          onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))}
        />
      )}
    </Field>
  );
}

/**
 * The card height: one number. The widget always takes exactly this height
 * on the page (clamped to a phone's screen) and scrolls inside, so the page
 * around it never moves. Written as min = max; empty means the default.
 */
function CardHeightField() {
  const size = usePath<PlacementSize>('placement.size');
  const issues = [...usePath('placement.size.max_height_px').issues, ...usePath('placement.size.min_height_px').issues];
  const value = size.value.max_height_px ?? size.value.min_height_px ?? CARD_HEIGHT_DEFAULT;
  const { min, max } = SIZE_LIMITS.max_height_px;
  const setHeight = (px: number | null) => size.set({ ...size.value, min_height_px: px, max_height_px: px });
  return (
    <Field
      label="Card height (px)"
      hint={`Your page never moves: the widget always takes exactly this much space, and longer steps scroll inside the card with the main button kept in view. ${CARD_HEIGHT_DEFAULT} suits most pages; ${min}–${max}. On a phone it is never taller than the screen, and a live conversation or chat opens full screen.`}
      issues={issues}
    >
      {(p) => (
        <div className="row gap-sm">
          <input
            {...p}
            className="input num"
            type="number"
            min={min}
            max={max}
            step={10}
            value={value}
            onChange={(e) => setHeight(e.target.value === '' ? CARD_HEIGHT_DEFAULT : Number(e.target.value))}
          />
          {value !== CARD_HEIGHT_DEFAULT ? (
            <button type="button" className="linkbtn" onClick={() => setHeight(CARD_HEIGHT_DEFAULT)}>
              Use {CARD_HEIGHT_DEFAULT} px
            </button>
          ) : null}
        </div>
      )}
    </Field>
  );
}

export function PlacementSizeSection() {
  const size = usePath<PlacementSize>('placement.size');
  const setKey = <K extends keyof PlacementSize>(key: K, v: PlacementSize[K]) => size.set({ ...size.value, [key]: v });
  return (
    <fieldset className="stack">
      <legend className="label" style={{ marginBottom: 8 }}>
        Card size
      </legend>
      <div className="field">
        <span className="small muted">Width</span>
        <Segmented
          label="Card width"
          value={size.value.width}
          onChange={(w) =>
            size.set({
              ...size.value,
              width: w,
              // width:max requires a cap; seed a sensible one instead of an error.
              // Any other width drops it: the widget caps `auto` at a set
              // max_width_px too, so a hidden leftover would still apply.
              max_width_px: w === 'max' ? (size.value.max_width_px ?? 560) : null,
            })
          }
          options={[
            { value: 'auto', label: 'Up to 420 px' },
            { value: 'fill', label: 'Fill the container' },
            { value: 'max', label: 'Up to a set width' },
          ]}
        />
      </div>
      <PxInput
        path="placement.size.max_width_px"
        label="Maximum width (px)"
        hint={`${SIZE_LIMITS.max_width_px.min}–${SIZE_LIMITS.max_width_px.max}. The card’s widest; your page element can be wider, and the card sits centred in it. Used with “Up to a set width”.`}
        disabled={size.value.width !== 'max'}
      />
      <CardHeightField />
      <div className="field">
        <span className="small muted">Density</span>
        <Segmented
          label="Density"
          value={size.value.density}
          onChange={(v) => setKey('density', v)}
          options={[
            { value: 'compact', label: 'Compact' },
            { value: 'regular', label: 'Regular' },
            { value: 'roomy', label: 'Roomy' },
          ]}
        />
        <span className="hint">Padding, heading size and control heights. Compact never shrinks body or consent text.</span>
      </div>
    </fieldset>
  );
}

export function PlacementSection() {
  const seed = usePath<boolean>('placement.seed_context_allowed');
  const token = usePath<boolean>('placement.identity_token_required');
  // Open when either is set away from its default, so a setting in effect is never hidden.
  const customised = !seed.value || token.value;
  return (
    <SectionCard id="sec-placement" title="Placement" description="How big the widget gets on your page. It appears inline, where you place its tag.">
      <PlacementSizeSection />
      <AdvancedDisclosure open={customised}>
        {/* ONE stored field (placement.seed_context_allowed) gates both page
            inputs in the widget (App.tsx: seedContext and the context-token
            prefill), so it is one switch that names both, not two switches
            that would secretly move together (third live review, 2026-09-27). */}
        <PathSwitch
          path="placement.seed_context_allowed"
          label="Accept details from your page"
          description={
            <>
              One setting with two effects: (1) your page can start the widget with a description of the problem, and (2) for signed-in visitors, the
              contact form can start filled in from your login (the fields ticked under Contact form → Fill from your login system). Turning it off
              stops both.
            </>
          }
        />
        <Disclosure label="How your developer uses it">
          <div className="stack-sm small">
            <p style={{ margin: 0 }}>
              A description goes in the tag’s <code>seed-context</code> attribute; the signed-in visitor’s details come in a signed <code>context-token</code>{' '}
              your server issues.
            </p>
            <CodeBlock
              caption="Example"
              code={'<finderly-diagnosis-widget site-key="fx_live_…" seed-context="Water heater leaking from the bottom"></finderly-diagnosis-widget>'}
            />
          </div>
        </Disclosure>
        <PathSwitch
          path="placement.identity_token_required"
          label="Only load for signed-in visitors"
          description="The widget won’t load unless your server passes a signed context-token with the visitor’s details. For customer portals behind a login."
        />
        <div className="notice info">
          <span>
            <strong>Analytics.</strong> The widget pushes <code>diagnosis_completed</code> after each diagnosis and <code>widget_handoff_click</code>{' '}
            when a result button is chosen to your page’s <code>dataLayer</code> (no diagnosis text or personal data), for Google Tag Manager and
            similar tools. Your tag manager decides whether anything fires.
          </span>
        </div>
      </AdvancedDisclosure>
    </SectionCard>
  );
}
